import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Grid,
  LoadingOverlay,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { CREATE_VEHICLE } from "apollo/mutuations";
import { IconUpload } from "@tabler/icons-react";

import {
  GET_DRIVERS,
  GET_REGIONS,
  GET_UNASSIGNED_DRIVERS,
  GET_VEHICLES,
  GET_VEHICLE_TYPES,
} from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import React, { useRef, useState, useMemo, useEffect } from "react";
import { MAX_FILE_SIZE } from "utiles/url";

const VehicleAddModal = ({ setOpened, driver_id,size,fetchData }) => {
  const form = useForm({
    initialValues: {
      model: "",
      plate_number: "",
      color: "",
      owner_name: "",
      owner_phone: "",
      driver: { connect: driver_id },
      region: { connect: 1 },
      vehicle_type: { connect: 1 },
      libre_number: "",
      license_number: "",
    },
  });

  const [vehicleTypeDropDownData, setVehicleTypeDropDownData] = useState([]);
  const [regionDropDownData, setRegionDropDownData] = useState([]);

  const { loading: vehicle_type_loading } = useQuery(GET_VEHICLE_TYPES, {
    variables: { first: 100 },
    onCompleted(data) {
      const newArr = data.vehicleTypes.data.map((element) => ({
        label: element.title,
        value: element.id,
      }));
      setVehicleTypeDropDownData(newArr);
    },
    onError() {
      showNotification({
        color: "red",
        title: "Error",
        message: "Something went wrong while fetching vehicle types!",
      });
      setOpened(false);
    },
  });

  const { loading: region_loading } = useQuery(GET_REGIONS, {
    variables: { first: 100, page: 1 },
    onCompleted(data) {
      const newArr = data.regions.data.map((element) => ({
        label: element.name,
        value: element.id,
      }));
      setRegionDropDownData(newArr);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || "Failed to get region",
      });
    },
  });

  const [addVehicle, { loading }] = useMutation(CREATE_VEHICLE, {
    onCompleted(data) {
      if (data.createVehicle) {
        showNotification({
          color: "green",
          title: "Success",
          message: "Vehicle Created Successfully",
        });
        setOpened(false);
        fetchData(size);
      }
    },
    onError(error) {
      setOpened(true);
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to create Vehicle",
      });
    },
  });

  const submit = () => {
    addVehicle({
      variables: {
        model: form.getInputProps("model").value,
        plate_number: form.getInputProps("plate_number").value,
        color: form.getInputProps("color").value,
        owner_name: form.getInputProps("owner_name").value,
        owner_phone: form.getInputProps("owner_phone").value,
        driver: form.getInputProps("driver").value,
        region: form.getInputProps("region").value,
        vehicle_type: form.getInputProps("vehicle_type").value,
        plate_doc: files.plate[0],
        license_doc: files.license[0],
        libre_doc: files.libre[0],
        business_certificate_doc: files.business_certificate[0],
        tin_certificate_doc: files.tin_certificate[0],
        license_number: form.getInputProps("license_number").value,
        libre_number: form.getInputProps("libre_number").value,
      },
    });
  };

  const setVehicleTypeDropDownValue = (val) => {
    form.setFieldValue("vehicle_type.connect", val);
  };

  const setRegionDropDownValue = (val) => {
    form.setFieldValue("region.connect", val);
  };

  const [files, setFiles] = useState({
    plate: [],
    license: [],
    libre: [],
    business_certificate: [],
    tin_certificate: [],
  });

  const fileInputRefs = {
    plate: useRef(null),
    license: useRef(null),
    libre: useRef(null),
    business_certificate: useRef(null),
    tin_certificate: useRef(null),
  };

  const handleFileChange = (event, fileType) => {
    const selectedFiles = Array.from(event.target.files);
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];

    const validFiles = selectedFiles.filter((file) => {
      if (!allowedTypes.includes(file.type)) {
        showNotification({
          color: "red",
          title: "Error",
          message: `File ${file.name} is not an accepted format. Allowed: JPG,JPEG PNG, PDF.`,
        });
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        showNotification({
          color: "red",
          title: "Error",
          message: `File ${file.name} exceeds 2MB. Please upload a smaller file.`,
        });
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setFiles((prevFiles) => ({
        ...prevFiles,
        [fileType]: validFiles,
      }));
    }
  };

  const FilePreview = ({ fileList }) => {
    const previews = useMemo(() => {
      return fileList.map((file, index) => {
        const fileUrl = URL.createObjectURL(file);

        return (
          <div key={index} style={{ margin: "10px auto" }}>
            {file.type === "application/pdf" ? (
              <embed
                src={fileUrl}
                width="130"
                height="160"
                type="application/pdf"
                onLoad={() => URL.revokeObjectURL(fileUrl)}
              />
            ) : (
              <img
                src={fileUrl}
                alt=""
                width="130"
                onLoad={() => URL.revokeObjectURL(fileUrl)}
              />
            )}
          </div>
        );
      });
    }, [fileList]);

    return (
      <SimpleGrid
        cols={3}
        spacing="md"
        style={{ maxHeight: "200px", overflowY: "auto" }} // Added fixed height and scroll
      >
        {previews}
      </SimpleGrid>
    );
  };

  const { height } = useViewportSize();

  return (
    <>
      <LoadingOverlay
        visible={loading || region_loading || vehicle_type_loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />

      <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(() => submit())}>
          <Stack>
            <Grid>
              <Grid.Col span={4}>
                <TextInput
                  placeholder="Model"
                  label="Model"
                  {...form.getInputProps("model")}
                  required
                />
                <TextInput
                  placeholder="Owner Phone"
                  label="Owner Phone"
                  {...form.getInputProps("owner_phone")}
                  required
                />
                <TextInput
                  placeholder="Plate Number"
                  label="Plate Number"
                  {...form.getInputProps("plate_number")}
                  reaquired
                />
                <TextInput
                  placeholder="Color"
                  label="Color"
                  {...form.getInputProps("color")}
                  required
                />
                <TextInput
                  placeholder="License Number"
                  label="License Number"
                  {...form.getInputProps("license_number")}
                  required
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  placeholder="Owner Name"
                  label="Owner Name"
                  {...form.getInputProps("owner_name")}
                  required
                />
                <Select
                  searchable
                  label="Select Vehicle Type"
                  required
                  placeholder="Pick one"
                  data={vehicleTypeDropDownData}
                  value={form
                    .getInputProps("vehicle_type.connect")
                    .value.toString()}
                  onChange={setVehicleTypeDropDownValue}
                />
                <Select
                  label="Select Region"
                  searchable
                  required
                  placeholder="Pick one"
                  data={regionDropDownData}
                  value={form.getInputProps("region.connect").value.toString()}
                  onChange={setRegionDropDownValue}
                />
                <TextInput
                  placeholder="Libre Number"
                  label="Libre Number"
                  {...form.getInputProps("libre_number")}
                  required
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <div
                  style={{
                    display: "flex",
                    marginTop: "20px",
                    justifyContent: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: "1 1 5%", margin: "5px" }}>
                    <Button
                      onClick={() => fileInputRefs.license.current.click()}
                      variant="outline"
                      leftIcon={<IconUpload size={16} />}
                    >
                      Upload License
                    </Button>
                    <input
                      type="file"
                      ref={fileInputRefs.license}
                      accept="image/png, image/jpeg, image/jpg,.pdf"
                      style={{ display: "none" }}
                      onChange={(event) => handleFileChange(event, "license")}
                    />
                    <FilePreview fileList={files.license} />
                  </div>

                  <div style={{ flex: "1 1 5%", margin: "5px" }}>
                    <Button
                      onClick={() => fileInputRefs.libre.current.click()}
                      variant="outline"
                      leftIcon={<IconUpload size={16} />}
                    >
                      Upload Libre
                    </Button>
                    <input
                      type="file"
                      ref={fileInputRefs.libre}
                      accept="image/png, image/jpeg, image/jpg,.pdf"
                      style={{ display: "none" }}
                      onChange={(event) => handleFileChange(event, "libre")}
                    />
                    <FilePreview fileList={files.libre} />
                  </div>

                  <div style={{ flex: "1 1 5%", margin: "5px" }}>
                    <Button
                      onClick={() =>
                        fileInputRefs.business_certificate.current.click()
                      }
                      variant="outline"
                      leftIcon={<IconUpload size={16} />}
                    >
                      Business Certificate
                    </Button>
                    <input
                      type="file"
                      ref={fileInputRefs.business_certificate}
                      accept="image/png, image/jpeg, image/jpg,.pdf"
                      style={{ display: "none" }}
                      onChange={(event) =>
                        handleFileChange(event, "business_certificate")
                      }
                    />
                    <FilePreview fileList={files.business_certificate} />
                  </div>

                  <div style={{ flex: "1 1 5%", margin: "5px" }}>
                    <Button
                      onClick={() =>
                        fileInputRefs.tin_certificate.current.click()
                      }
                      variant="outline"
                      leftIcon={<IconUpload size={16} />}
                    >
                      TIN Certificate
                    </Button>
                    <input
                      type="file"
                      ref={fileInputRefs.tin_certificate}
                      accept="image/png, image/jpeg, image/jpg,.pdf"
                      style={{ display: "none" }}
                      onChange={(event) =>
                        handleFileChange(event, "tin_certificate")
                      }
                    />
                    <FilePreview fileList={files.tin_certificate} />
                  </div>
                </div>
              </Grid.Col>
            </Grid>
          </Stack>

          <Grid>
            <Grid.Col span={4}>
              <Button
                type="submit"
                style={{
                  marginTop: "20px",
                  width: "25%",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
                fullWidth
                color="blue"
              >
                Submit
              </Button>
            </Grid.Col>
          </Grid>
        </form>
      </ScrollArea>
    </>
  );
};

export default VehicleAddModal;
